/**
 * Team page — Studio members + pending invites + role management.
 *
 * Layout (matches design folder `team.html` lines ~130-200):
 *   Header: "Studio Team" + count caption + "+ Add Member" CTA
 *   Summary tile: Total / Active / Pending counts
 *   Filter chips: All / role-bucket (Admin / Manager / Site / Client …)
 *                 + search input
 *   Active members table — 7 cols: Member · Role · Phone · Email
 *                                   · Status · Action menu
 *   Pending Invites table (if any) — invite phone · role · invited-by ·
 *                                     revoke button
 *
 * Click row → opens MemberDetailSlideOver (change role + remove).
 * "+ Add Member" → opens InviteMemberSlideOver.
 *
 * Capability gate — only the org owner + admins can invite / change
 * roles / remove. Other roles see the team list (read-only) without
 * the action affordances. Server enforces the same gate.
 */
import { useEffect, useMemo, useState } from 'react';

import { canManageProjects, isAdminish } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { InviteMemberSlideOver } from '@/components/InviteMemberSlideOver';
import {
  MemberDetailSlideOver,
  type MemberDetailMode,
} from '@/components/MemberDetailSlideOver';
import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import { useFullOrganization } from '@/lib/useFullOrganization';
import {
  ROLE_TONE,
  memberInitials,
  useOrgMembers,
  type OrgMember,
} from '@/lib/useOrgMembers';
import { usePendingInvites, type PendingInvite } from '@/lib/usePendingInvites';
import {
  ROLE_LABELS,
  type RoleKey,
} from '@/lib/useProjectMembers';

// Filter chip buckets. Roles map → chip key.
type FilterKey =
  | 'all'
  | 'admin'      // superAdmin + admin
  | 'manager'
  | 'site'       // siteEngineer + supervisor
  | 'design'     // designer + accountant
  | 'client'     // client + viewer
  | 'pending';

const CHIPS: { key: FilterKey; label: string }[] = [
  { key: 'all',     label: 'All' },
  { key: 'admin',   label: 'Admins' },
  { key: 'manager', label: 'Managers' },
  { key: 'site',    label: 'Site team' },
  { key: 'design',  label: 'Design & finance' },
  { key: 'client',  label: 'Clients & viewers' },
  { key: 'pending', label: 'Pending invites' },
];

function roleMatchesChip(role: RoleKey, chip: FilterKey): boolean {
  switch (chip) {
    case 'all':     return true;
    case 'admin':   return isAdminish(role);
    case 'manager': return role === 'manager';
    case 'site':    return role === 'siteEngineer' || role === 'supervisor';
    case 'design':  return role === 'designer' || role === 'accountant';
    case 'client':  return role === 'client' || role === 'viewer';
    case 'pending': return false;
  }
}

function relTime(ms: number | null): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
}

export function TeamPage() {
  const { user } = useAuth();
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const orgName = (org?.data?.name as string | undefined) ?? 'Your Studio';

  const { members, loading: membersLoading } = useOrgMembers(orgId);
  const { invites, loading: invitesLoading } = usePendingInvites(orgId);

  const { query: searchQuery, setPlaceholder } = useTopbarSearch();
  useEffect(() => {
    setPlaceholder('Search team by name, phone, email…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  const [chip, setChip] = useState<FilterKey>('all');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [detailMode, setDetailMode] = useState<MemberDetailMode>({
    kind: 'closed',
  });

  // Caller capability: SA / Admin / Manager can manage the team.
  // The role-picker chip list + Remove button visibility inside the
  // MemberDetailSlideOver are further narrowed per caller role —
  // Manager can only assign sub-roles + cannot edit/remove peers.
  const callerRole: RoleKey | null = useMemo(() => {
    if (!user || !org) return null;
    const orgData = org.data as {
      ownerId?: string;
      roles?: Record<string, string>;
    };
    if (orgData.ownerId === user.uid) return 'superAdmin';
    const r = orgData.roles?.[user.uid];
    return (r as RoleKey | undefined) ?? null;
  }, [user, org]);
  const canManage = canManageProjects(callerRole);

  // Search filter (applied across BOTH active + pending lists).
  const q = (searchQuery ?? '').trim().toLowerCase();
  const matchesSearch = (haystack: string) =>
    !q || haystack.toLowerCase().includes(q);

  const filteredMembers = useMemo(() => {
    if (chip === 'pending') return [];
    return members.filter((m) => {
      if (!roleMatchesChip(m.role, chip)) return false;
      const hay = [m.displayName, m.phoneNumber ?? '', m.email ?? ''].join(' ');
      return matchesSearch(hay);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, chip, q]);

  const filteredInvites = useMemo(() => {
    if (chip !== 'all' && chip !== 'pending') return [];
    return invites.filter((i) =>
      matchesSearch(
        [i.displayName ?? '', i.phoneNumber, ROLE_LABELS[i.role]].join(' '),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invites, chip, q]);

  // Summary numbers (always from the full snapshot, not the filter).
  const summary = useMemo(() => {
    return {
      total: members.length + invites.length,
      active: members.length,
      pending: invites.length,
    };
  }, [members, invites]);

  return (
    <div className="page active" id="page-team" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Studio Team</h1>
          <div className="projects-stats">
            {orgName} ·{' '}
            {summary.active} active member{summary.active === 1 ? '' : 's'}
            {summary.pending > 0
              ? ` · ${summary.pending} pending invite${summary.pending === 1 ? '' : 's'}`
              : ''}
          </div>
        </div>
        {canManage ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setInviteOpen(true)}
            disabled={!orgId}
          >
            <i className="ph-bold ph-plus" /> Add Member
          </button>
        ) : null}
      </div>

      {/* Summary tile (matches Laminate Overview pattern) */}
      <div
        className="card"
        style={{
          padding: 0,
          overflow: 'hidden',
          marginBottom: 16,
          maxWidth: 480,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 0,
            padding: 14,
          }}
        >
          <SummaryCell label="TOTAL" value={summary.total} />
          <Divider />
          <SummaryCell label="ACTIVE" value={summary.active} accent="var(--success)" />
          <Divider />
          <SummaryCell
            label="PENDING"
            value={summary.pending}
            accent={summary.pending > 0 ? 'var(--warning)' : undefined}
          />
        </div>
      </div>

      {/* Filter chips */}
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        {CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`filter-chip${chip === c.key ? ' active' : ''}`}
            onClick={() => setChip(c.key)}
          >
            {c.label}
            {c.key === 'pending' && summary.pending > 0 ? (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: 'var(--warning-bg)',
                  color: 'var(--warning)',
                }}
              >
                {summary.pending}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Active members table */}
      {chip !== 'pending' ? (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
          <div
            className="card-header"
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            <div className="card-title">
              Active Members{' '}
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--text-tertiary)',
                  fontWeight: 500,
                }}
              >
                · {filteredMembers.length}
              </span>
            </div>
          </div>

          {membersLoading && filteredMembers.length === 0 ? (
            <div
              style={{
                padding: 48,
                textAlign: 'center',
                color: 'var(--text-secondary)',
              }}
            >
              Loading team…
            </div>
          ) : filteredMembers.length === 0 ? (
            <div
              style={{
                padding: 48,
                textAlign: 'center',
                color: 'var(--text-tertiary)',
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.4 }}>
                <i className="ph ph-users-three" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {q ? 'No members match your search.' : 'No members in this filter.'}
              </div>
            </div>
          ) : (
            <div className="proj-table-wrap">
              <table className="proj-table">
                <thead>
                  <tr>
                    <th style={{ width: 280 }}>Member</th>
                    <th>Role</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Joined</th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((m) => (
                    <MemberRow
                      key={m.uid}
                      m={m}
                      onClick={() =>
                        setDetailMode({ kind: 'member', member: m })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* Pending invites */}
      {(chip === 'all' || chip === 'pending') &&
      (filteredInvites.length > 0 || invitesLoading) ? (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div
            className="card-header"
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            <div className="card-title">
              Pending Invites{' '}
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--text-tertiary)',
                  fontWeight: 500,
                }}
              >
                · {filteredInvites.length}
              </span>
            </div>
          </div>
          {invitesLoading && filteredInvites.length === 0 ? (
            <div
              style={{
                padding: 32,
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: 13,
              }}
            >
              Loading…
            </div>
          ) : (
            <div className="proj-table-wrap">
              <table className="proj-table">
                <thead>
                  <tr>
                    <th style={{ width: 280 }}>Invitee</th>
                    <th>Role</th>
                    <th>Projects</th>
                    <th>Invited</th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {filteredInvites.map((i) => (
                    <InviteRow
                      key={i.phoneNumber}
                      i={i}
                      onClick={() =>
                        setDetailMode({ kind: 'invite', invite: i })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* Slide-overs */}
      <InviteMemberSlideOver
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        orgId={orgId}
      />
      <MemberDetailSlideOver
        mode={detailMode}
        orgId={orgId}
        onClose={() => setDetailMode({ kind: 'closed' })}
        readOnly={!canManage}
        callerRole={callerRole}
      />
    </div>
  );
}

// ───── Row components ─────

function MemberRow({
  m,
  onClick,
}: {
  m: OrgMember;
  onClick: () => void;
}) {
  const tone = ROLE_TONE[m.role];
  return (
    <tr
      onClick={onClick}
      className="clickable-row"
      style={{ cursor: 'pointer' }}
    >
      <td>
        <div className="proj-name-cell">
          <div
            className="proj-thumb"
            style={{
              background: m.photoURL
                ? `center / cover no-repeat url(${m.photoURL})`
                : tone.bg,
              color: tone.fg,
            }}
          >
            {m.photoURL ? '' : memberInitials(m.displayName)}
          </div>
          <div className="proj-name-text">
            <div className="proj-name-title">
              {m.displayName}
              {m.isOwner ? (
                <i
                  className="ph-fill ph-crown"
                  style={{
                    color: '#D97706',
                    marginLeft: 6,
                    fontSize: 12,
                  }}
                  title="Studio owner"
                />
              ) : null}
            </div>
            <div className="proj-name-client">
              {m.isOwner ? 'Studio owner' : ROLE_LABELS[m.role]}
            </div>
          </div>
        </div>
      </td>
      <td>
        <span
          className="stage-pill"
          style={{
            background: tone.bg,
            color: tone.fg,
            fontWeight: 700,
            fontSize: 10,
            padding: '2px 8px',
          }}
        >
          {ROLE_LABELS[m.role]}
        </span>
      </td>
      <td style={{ fontFamily: 'Menlo, monospace', fontSize: 12 }}>
        {m.phoneNumber ?? '—'}
      </td>
      <td
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {m.email ?? '—'}
      </td>
      <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
        {relTime(m.joinedAtMs)}
      </td>
      <td style={{ textAlign: 'center' }}>
        <i
          className="ph ph-caret-right"
          style={{ color: 'var(--text-tertiary)' }}
        />
      </td>
    </tr>
  );
}

function InviteRow({
  i,
  onClick,
}: {
  i: PendingInvite;
  onClick: () => void;
}) {
  const tone = ROLE_TONE[i.role];
  const name = i.displayName ?? i.phoneNumber;
  return (
    <tr
      onClick={onClick}
      className="clickable-row"
      style={{ cursor: 'pointer' }}
    >
      <td>
        <div className="proj-name-cell">
          <div
            className="proj-thumb"
            style={{
              background: '#FAFAFA',
              color: 'var(--text-tertiary)',
              border: '1px dashed var(--border)',
            }}
          >
            {memberInitials(name)}
          </div>
          <div className="proj-name-text">
            <div className="proj-name-title">{name}</div>
            <div className="proj-name-client">
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 999,
                  background: '#FEF3C7',
                  color: '#B45309',
                  letterSpacing: 0.4,
                }}
              >
                INVITE PENDING
              </span>
            </div>
          </div>
        </div>
      </td>
      <td>
        <span
          className="stage-pill"
          style={{
            background: tone.bg,
            color: tone.fg,
            fontWeight: 700,
            fontSize: 10,
            padding: '2px 8px',
          }}
        >
          {ROLE_LABELS[i.role]}
        </span>
      </td>
      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {i.projectIds.length
          ? `${i.projectIds.length} project${i.projectIds.length === 1 ? '' : 's'}`
          : 'All projects'}
      </td>
      <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
        {relTime(i.invitedAtMs)}
      </td>
      <td style={{ textAlign: 'center' }}>
        <i
          className="ph ph-caret-right"
          style={{ color: 'var(--text-tertiary)' }}
        />
      </td>
    </tr>
  );
}

// ───── Summary tile helpers ─────

function SummaryCell({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: accent ?? 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          letterSpacing: 0.5,
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        width: 1,
        background: 'var(--border)',
        marginLeft: 8,
        marginRight: 8,
      }}
    />
  );
}
